import React, { useEffect, useRef, useState } from 'react';

/**
 * ScrollReveal — Animate elements when they scroll into view
 * Uses Intersection Observer (no library needed)
 * 
 * Props:
 * - animation: 'fadeUp' | 'fadeDown' | 'fadeLeft' | 'fadeRight' | 'scaleUp' | 'fadeIn'
 * - delay: delay in ms (default 0)
 * - duration: animation duration in ms (default 600)
 * - threshold: visibility threshold (default 0.15)
 * - once: animate only once (default true)
 * - className: additional classes
 * - stagger: stagger children by this many ms (0 = no stagger)
 * - as: wrapper element type (default 'div')
 */
const animations = {
    fadeUp: {
        hidden: { opacity: 0, transform: 'translateY(40px)' },
        visible: { opacity: 1, transform: 'translateY(0)' },
    },
    fadeDown: {
        hidden: { opacity: 0, transform: 'translateY(-40px)' },
        visible: { opacity: 1, transform: 'translateY(0)' },
    },
    fadeLeft: {
        hidden: { opacity: 0, transform: 'translateX(-40px)' },
        visible: { opacity: 1, transform: 'translateX(0)' },
    },
    fadeRight: {
        hidden: { opacity: 0, transform: 'translateX(40px)' },
        visible: { opacity: 1, transform: 'translateX(0)' },
    },
    scaleUp: {
        hidden: { opacity: 0, transform: 'scale(0.9)' },
        visible: { opacity: 1, transform: 'scale(1)' },
    },
    fadeIn: {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    },
};

function ScrollReveal({
    children,
    animation = 'fadeUp',
    delay = 0,
    duration = 600,
    threshold = 0.15,
    once = true,
    className = '',
    as: Tag = 'div',
    style = {},
}) {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (once) observer.unobserve(el);
                } else if (!once) {
                    setIsVisible(false);
                }
            },
            { threshold, rootMargin: '0px 0px -40px 0px' }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [threshold, once]);

    const anim = animations[animation] || animations.fadeUp;
    const currentState = isVisible ? anim.visible : anim.hidden;

    return (
        <Tag
            ref={ref}
            className={className}
            style={{
                ...style,
                ...currentState,
                transition: `all ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
                willChange: 'opacity, transform',
            }}
        >
            {children}
        </Tag>
    );
}

/**
 * StaggerChildren — Wraps children and applies staggered scroll reveal
 */
function StaggerChildren({
    children,
    animation = 'fadeUp',
    staggerDelay = 100,
    duration = 600,
    threshold = 0.1,
    className = '',
    childClassName = '',
}) {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(el);
                }
            },
            { threshold, rootMargin: '0px 0px -40px 0px' }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [threshold]);

    const anim = animations[animation] || animations.fadeUp;

    return (
        <div ref={ref} className={className}>
            {React.Children.map(children, (child, index) => {
                if (!React.isValidElement(child)) return child;
                const currentState = isVisible ? anim.visible : anim.hidden;
                return (
                    <div
                        className={childClassName}
                        style={{
                            ...currentState,
                            transition: `all ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${index * staggerDelay}ms`,
                            willChange: 'opacity, transform',
                        }}
                    >
                        {child}
                    </div>
                );
            })}
        </div>
    );
}

export { ScrollReveal, StaggerChildren };
export default ScrollReveal;
